/**
 * Applies user display preferences to the document root as data-attributes.
 *
 * CSS hooks live in ./settings.css:
 * - [data-reduced-motion='true']  — collapses animations/transitions
 * - [data-high-contrast='true']   — strengthens text/border contrast
 * - [data-text-size='small'|'normal'|'large'] — scales the root font size
 *
 * Call applyDisplayPreferences() on settings load and on every change; the
 * effect is immediate and does not require a reload.
 */
import type { UserSettings } from '@/types'

export type DisplayPreferences = Pick<UserSettings, 'reducedMotion' | 'highContrast' | 'textSize'>

export const DEFAULT_DISPLAY_PREFERENCES: DisplayPreferences = {
  reducedMotion: false,
  highContrast: false,
  textSize: 'normal',
}

const TEXT_SIZES: readonly DisplayPreferences['textSize'][] = ['small', 'normal', 'large']

/** Applies the preferences to <html> data-attributes. Safe to call repeatedly. */
export function applyDisplayPreferences(preferences: DisplayPreferences): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.reducedMotion = preferences.reducedMotion ? 'true' : 'false'
  root.dataset.highContrast = preferences.highContrast ? 'true' : 'false'
  root.dataset.textSize = TEXT_SIZES.includes(preferences.textSize) ? preferences.textSize : 'normal'
}
